import {UnitType} from '@benchling/units';

import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {BenchlingDuotoneIconName} from 'ui-library/icons/BenchlingDuotoneIcon';
import {getJSONPromise, patchJSONPromise, postJSONPromise} from 'util/Net';

export const UNIT_DICTIONARY_URL = '/units/listing';

// Matches shapes in benchling/units/network/rest/unit_rest_specs.py
export interface CreateUnitParams {
  name: string;
  symbol: string;
  aliases?: Array<string>;
  unitTypeId: string;
  conversionFactor: string;
}

export interface CreateResponse {
  createdIds: Array<string>;
}

export interface UpdateUnitParams {
  id: string;
  name?: string;
  symbol?: string;
  conversionFactor?: string;
}

export interface UpdateResponse {
  updatedIds: Array<string>;
}

export interface CreateUnitTypeParams {
  name: string;
  terms: Array<{value: string; exponent: number}>;
  baseUnit: {
    name: string;
    symbol: string;
    aliases?: Array<string>;
    siName?: string | null;
  };
  formula?: Array<{value: string; exponent: number}>;
}

export interface UpdateUnitTypeParams {
  id: string;
  name?: string;
  terms?: Array<{value: string; exponent: number}>;
  formula?: Array<{value: string; exponent: number}>;
  overrideIsConcentration?: boolean;
  overrideIsContainerQuantity?: boolean;
}

export function createUnit(unit: CreateUnitParams): Promise<CreateResponse> {
  const creates = [unit];
  return postJSONPromise({url: `${getApiBaseForObjectType(DomainGraphType.UNIT)}`, data: {creates}});
}

export function updateUnit(unit: UpdateUnitParams): Promise<UpdateResponse> {
  const updates = [unit];
  return patchJSONPromise({url: `${getApiBaseForObjectType(DomainGraphType.UNIT)}`, data: {updates}});
}

export function createUnitType(unitType: CreateUnitTypeParams): Promise<CreateResponse> {
  const creates = [unitType];
  return postJSONPromise({url: `${getApiBaseForObjectType(DomainGraphType.UNIT_TYPE)}`, data: {creates}});
}

export function updateUnitType(unitType: UpdateUnitTypeParams): Promise<UpdateResponse> {
  const updates = [unitType];
  return patchJSONPromise({url: `${getApiBaseForObjectType(DomainGraphType.UNIT_TYPE)}`, data: {updates}});
}

// Keep in sync with benchling/units/network/rest/onboarding_units_rest_specs.py

export interface OnboardingUnit {
  name: string;
  symbol: string;
  conversionFactor: string;
  defaultSelected?: boolean;
  aliases?: Array<string>;
  siName?: string | null;
}

export interface OnboardingUnitType extends UnitType {
  name: string;
  icon?: BenchlingDuotoneIconName;
}
export interface OnboardingUnitTypeAndUnitsData {
  unitType: OnboardingUnitType;
  required: boolean;
  baseUnitSymbol: string;
  rank: number;
  units: Array<OnboardingUnit>;
  formula?: Array<{value: string; exponent: number}>;
}

export interface FetchUnitsOnboardingDataResponse {
  siUnitsData: Array<OnboardingUnitTypeAndUnitsData>;
  pdUnitsData: Array<OnboardingUnitTypeAndUnitsData>;
}

export function fetchUnitsOnboardingData(): Promise<FetchUnitsOnboardingDataResponse> {
  return getJSONPromise('/1/api/onboarding-units');
}

export interface OnboardingUnitCreateParams {
  name: string;
  symbol: string;
  aliases?: Array<string>;
  conversionFactor: string;
}

export interface OnboardingUnitTypeAndUnitsCreateParams {
  unitType: CreateUnitTypeParams; // Note that the formula terms here use unit symbols, not ids
  units: Array<OnboardingUnitCreateParams>;
  rank: number;
}

export function createOnboardingUnitTypesAndUnits(
  creates: Array<OnboardingUnitTypeAndUnitsCreateParams>
): Promise<void> {
  return postJSONPromise({url: '/1/api/onboarding-units', data: {creates}});
}

export interface GetSchemaFieldCountsResponse {
  schemaFieldCount: number;
}

export function getSchemaFieldCounts(unitIds: Array<string>): Promise<GetSchemaFieldCountsResponse> {
  return getJSONPromise('/1/api/units/schema-field-counts', {unitIds});
}
